// ============================================
// AccountMenu — header avatar + sign-in / account popover (W4a)
// ============================================
//
// Renders ONLY when VITE_FEATURE_CLOUD_SYNC === 'true'.
//
// Logged-out: avatar shows a sign-in icon; click opens the auth surface
// (Modal reused) offering Google OAuth and a two-step email-OTP flow.
//
// Logged-in: avatar shows the user's initial (if email is available) or
// a generic signed-in icon; click opens a small status + sign-out menu.
//
// Auth actions are reached via getSyncService() — the singleton holds
// passthrough methods delegating to CloudStore (signInWithGoogle,
// signInWithOtp, verifyOtp, signOut). This keeps the Supabase chunk
// out of this module's static imports and lets the same lazy chunk
// handle both the service AND the auth calls.

import { useState } from 'preact/hooks';
import { currentUser, syncStatus } from '@/state/signals';
import { Modal } from '@/components/ui';

// ── Lazy service accessor ──────────────────────────────────────────────────
// We do NOT statically import SyncService here — a static import would pull
// the module (and its dynamic cloudStore import) into the main bundle entry,
// defeating the lazy-chunk goal. Instead we use a dynamic import that resolves
// to the already-initialised singleton (App.tsx calls initSync() at idle time
// before the user can click the avatar, so the singleton is ready in practice).
async function getService() {
  const { getSyncService } = await import('@/services/SyncService');
  return getSyncService();
}

// ============================================
// Icons (inline SVG — no extra asset)
// ============================================

function SignInIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="w-5 h-5"
      aria-hidden="true"
    >
      {/* person outline */}
      <circle cx="12" cy="8" r="4" />
      <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
    </svg>
  );
}

function SignedInIcon({ initial }: { initial?: string }) {
  if (initial) {
    return (
      <span
        className="flex items-center justify-center w-full h-full text-sm font-bold leading-none select-none"
        aria-hidden="true"
      >
        {initial}
      </span>
    );
  }
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="currentColor"
      className="w-5 h-5"
      aria-hidden="true"
    >
      <circle cx="12" cy="8" r="4" />
      <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
    </svg>
  );
}

// ============================================
// Sync-status label
// ============================================

function SyncStatusLabel() {
  const status = syncStatus.value;
  const labels: Record<typeof status, string> = {
    idle: 'Synced',
    syncing: 'Syncing…',
    error: 'Sync error',
    offline: 'Offline'
  };
  const colors: Record<typeof status, string> = {
    idle: 'text-emerald-600',
    syncing: 'text-blue-500',
    error: 'text-red-500',
    offline: 'text-amber-500'
  };
  return (
    <span className={`text-xs font-medium ${colors[status]}`}>
      {labels[status]}
    </span>
  );
}

// ============================================
// Two-step OTP state machine
// email → code
// ============================================

type OtpStep = 'email' | 'code';

function OtpForm({ onSuccess, onError }: { onSuccess: () => void; onError: (msg: string) => void }) {
  const [step, setStep] = useState<OtpStep>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSendCode = async (e: Event) => {
    e.preventDefault();
    setLoading(true);
    try {
      const svc = await getService();
      if (!svc) { onError('Sync not ready — please try again.'); return; }
      await svc.signInWithOtp(email.trim());
      setStep('code');
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to send code.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e: Event) => {
    e.preventDefault();
    setLoading(true);
    try {
      const svc = await getService();
      if (!svc) { onError('Sync not ready — please try again.'); return; }
      await svc.verifyOtp(email.trim(), code.trim());
      onSuccess();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Invalid or expired code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (step === 'email') {
    return (
      <form onSubmit={handleSendCode} className="space-y-3">
        <div>
          <label
            htmlFor="otp-email"
            className="block text-sm font-medium text-slate-700 mb-1"
          >
            Email address
          </label>
          <input
            id="otp-email"
            type="email"
            required
            autoComplete="email"
            value={email}
            onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
            placeholder="you@example.com"
            className="
              w-full px-3 py-2 rounded-lg border border-slate-300
              text-sm text-slate-800 placeholder-slate-400
              focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent
              disabled:opacity-50
            "
            disabled={loading}
          />
        </div>
        <button
          type="submit"
          disabled={loading || !email.trim()}
          className="
            w-full px-4 py-2 rounded-lg font-semibold text-sm
            btn-primary disabled:opacity-50 disabled:cursor-not-allowed
            transition-all
          "
          aria-busy={loading}
        >
          {loading ? 'Sending…' : 'Send code'}
        </button>
      </form>
    );
  }

  // step === 'code'
  return (
    <form onSubmit={handleVerify} className="space-y-3">
      <p className="text-sm text-slate-600">
        Enter the 6-digit code from your email.{' '}
        <span className="text-slate-400 text-xs">
          (Check your spam folder if it doesn't arrive.)
        </span>
      </p>
      <div>
        <label
          htmlFor="otp-code"
          className="block text-sm font-medium text-slate-700 mb-1"
        >
          Verification code
        </label>
        <input
          id="otp-code"
          type="text"
          inputMode="numeric"
          pattern="[0-9]{6}"
          maxLength={6}
          required
          autoComplete="one-time-code"
          value={code}
          onInput={(e) => setCode((e.target as HTMLInputElement).value)}
          placeholder="123456"
          className="
            w-full px-3 py-2 rounded-lg border border-slate-300
            text-sm text-slate-800 placeholder-slate-400 tracking-widest font-mono
            focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent
            disabled:opacity-50
          "
          disabled={loading}
        />
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => { setStep('email'); setCode(''); }}
          disabled={loading}
          className="
            flex-1 px-3 py-2 rounded-lg text-sm font-semibold
            bg-slate-100 text-slate-600 hover:bg-slate-200
            disabled:opacity-50 transition-all
          "
        >
          Back
        </button>
        <button
          type="submit"
          disabled={loading || code.trim().length < 6}
          className="
            flex-1 px-3 py-2 rounded-lg font-semibold text-sm
            btn-primary disabled:opacity-50 disabled:cursor-not-allowed
            transition-all
          "
          aria-busy={loading}
        >
          {loading ? 'Verifying…' : 'Verify'}
        </button>
      </div>
    </form>
  );
}

// ============================================
// Sign-in surface (inside Modal)
// ============================================

function SignInContent({ onClose }: { onClose: () => void }) {
  const [authError, setAuthError] = useState<string | null>(null);
  const [googleLoading, setGoogleLoading] = useState(false);

  const handleGoogle = async () => {
    setAuthError(null);
    setGoogleLoading(true);
    try {
      const svc = await getService();
      if (!svc) { setAuthError('Sync not ready — please try again.'); setGoogleLoading(false); return; }
      await svc.signInWithGoogle();
      // redirect happens — no onClose needed
    } catch (err) {
      setAuthError(err instanceof Error ? err.message : 'Google sign-in failed.');
      setGoogleLoading(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Google OAuth */}
      <button
        type="button"
        onClick={handleGoogle}
        disabled={googleLoading}
        aria-busy={googleLoading}
        className="
          w-full flex items-center justify-center gap-3
          px-4 py-2.5 rounded-lg border border-slate-300
          bg-white hover:bg-slate-50 text-slate-700 font-semibold text-sm
          shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed
          focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400
        "
      >
        {/* Google "G" icon */}
        <svg viewBox="0 0 24 24" className="w-5 h-5 flex-shrink-0" aria-hidden="true">
          <path
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            fill="#4285F4"
          />
          <path
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            fill="#34A853"
          />
          <path
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            fill="#FBBC05"
          />
          <path
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            fill="#EA4335"
          />
        </svg>
        {googleLoading ? 'Redirecting…' : 'Continue with Google'}
      </button>

      {/* Email OTP — gated behind VITE_FEATURE_EMAIL_OTP. Hidden until a
          custom SMTP (Resend) + the {{ .Token }} template are configured;
          otherwise Supabase emails a magic link, not the 6-digit code this
          form expects. Flip the flag on once SMTP is ready — no code change. */}
      {import.meta.env.VITE_FEATURE_EMAIL_OTP === 'true' && (
        <>
          {/* Divider */}
          <div className="flex items-center gap-3">
            <div className="flex-1 border-t border-slate-200" />
            <span className="text-xs text-slate-400 font-medium">or email</span>
            <div className="flex-1 border-t border-slate-200" />
          </div>

          {/* Email OTP */}
          <OtpForm
            onSuccess={onClose}
            onError={(msg) => setAuthError(msg)}
          />
        </>
      )}

      {authError && (
        <p role="alert" className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">
          {authError}
        </p>
      )}
    </div>
  );
}

// ============================================
// Signed-in popover content
// ============================================

function SignedInContent({ onClose }: { onClose: () => void }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSignOut = async () => {
    setLoading(true);
    setError(null);
    try {
      const svc = await getService();
      if (!svc) { setLoading(false); return; }
      await svc.signOut();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign out failed.');
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <span className="text-sm text-slate-600">Sync status:</span>
        <SyncStatusLabel />
      </div>
      <p className="text-xs text-slate-500">
        Your progress syncs across devices automatically when you're signed in.
        Signing out keeps your local progress intact.
      </p>
      {error && (
        <p role="alert" className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">
          {error}
        </p>
      )}
      <button
        type="button"
        onClick={handleSignOut}
        disabled={loading}
        aria-busy={loading}
        className="
          w-full px-4 py-2 rounded-lg text-sm font-semibold
          bg-slate-100 text-slate-700 hover:bg-slate-200
          disabled:opacity-50 disabled:cursor-not-allowed transition-all
          focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400
        "
      >
        {loading ? 'Signing out…' : 'Sign out'}
      </button>
    </div>
  );
}

// ============================================
// AccountMenuInner — rendered only when flag is on
// ============================================

// ============================================
// SyncChip — small inline status chip shown next to the avatar when signed in
// ============================================
//
// Reads the syncStatus signal and renders a compact, colour-coded label so the
// user can see "Synced ✓" / "Syncing…" / "Sync error" / "Offline" at a glance
// without opening the popover. Visible at all viewport widths.

function SyncChip() {
  const status = syncStatus.value;
  const labels: Record<typeof status, string> = {
    idle: 'Synced',
    syncing: 'Syncing…',
    error: 'Sync error',
    offline: 'Offline'
  };
  // Plain colour-coded text — no background/border. The dark-pill-wrapping-a-
  // green-pill look was too heavy on the light header; a single tinted label
  // beside the avatar stays legible without the visual weight.
  const textColor: Record<typeof status, string> = {
    idle: 'text-emerald-600',
    syncing: 'text-blue-600',
    error: 'text-red-600',
    offline: 'text-amber-600'
  };
  return (
    <span
      className={`
        inline-flex items-center gap-0.5
        text-xs font-medium leading-none
        ${textColor[status]}
        select-none whitespace-nowrap
      `}
      aria-live="polite"
      aria-label={`Sync status: ${labels[status]}`}
    >
      {labels[status]}
      {status === 'idle' && (
        <svg viewBox="0 0 16 16" className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M3.5 8.5l3 3 6-7" />
        </svg>
      )}
    </span>
  );
}

function AccountMenuInner() {
  const userId = currentUser.value;
  const [isOpen, setIsOpen] = useState(false);

  // For the avatar initial we only have the userId (UUID), not the email,
  // from the signal (SyncService exposes userId, not profile data). When
  // the user is signed in we show a generic "person + cloud" avatar.
  const isLoggedIn = userId !== null;

  const handleOpen = () => setIsOpen(true);
  const handleClose = () => setIsOpen(false);

  return (
    <>
      {/* Avatar button + optional status chip — flex row so the chip sits
          inline next to the avatar without breaking the header's gap layout */}
      <button
        type="button"
        onClick={handleOpen}
        aria-label={isLoggedIn ? 'Account — manage sync' : 'Sign in to sync progress'}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        className="
          flex items-center gap-2
          pl-1 pr-2.5 py-1 rounded-full
          transition-colors hover:bg-slate-100
          focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2
        "
      >
        {/* Avatar dot — small colour-coded circle, the identity marker.
            Indigo when signed in, neutral grey when signed out. Fixed size
            so the control doesn't reflow when sync status changes. */}
        <span
          className={`
            flex items-center justify-center w-7 h-7 flex-shrink-0 rounded-full
            ${isLoggedIn ? 'bg-primary-500 text-white' : 'bg-slate-200 text-slate-500'}
          `}
        >
          {isLoggedIn ? <SignedInIcon /> : <SignInIcon />}
        </span>

        {/* Signed-in: lightweight tinted "Synced" label (legible, not a heavy chip).
            Signed-out: "Sign in" text so new users find the entry point without
            relying on the OnboardingHint squiggle. */}
        {isLoggedIn ? (
          <SyncChip />
        ) : (
          <span
            className="text-xs font-semibold text-slate-600 select-none whitespace-nowrap"
            aria-hidden="true"  /* aria-label on the button already says "Sign in" */
          >
            Sign in
          </span>
        )}
      </button>

      {/* Modal — reuses existing ui/Modal with focus trap built-in */}
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title={isLoggedIn ? 'Your account' : 'Sign in to sync'}
        size="sm"
      >
        {isLoggedIn ? (
          <SignedInContent onClose={handleClose} />
        ) : (
          <SignInContent onClose={handleClose} />
        )}
      </Modal>
    </>
  );
}

// ============================================
// AccountMenu — public export (flag-guarded wrapper)
// ============================================
//
// The flag check lives in the wrapper (not AccountMenuInner) so that hooks
// inside AccountMenuInner are always called unconditionally — satisfying the
// react-hooks/rules-of-hooks lint rule.

export function AccountMenu() {
  if (import.meta.env.VITE_FEATURE_CLOUD_SYNC !== 'true') return null;
  return <AccountMenuInner />;
}
